/**
 * The pure merge/dedupe rules behind the Family Command Center's ONE
 * "Waiting on you" list (docs/feed-unification-plan-2026-08-18.md, Slice B;
 * DECISIONS.md 2026-08-18, "Everything is a message" and its same-night
 * follow-ups). FamilyCommandCenter.tsx used to run a separate "New alerts"
 * counter and panel off the raw notification stream; this file is what
 * decides which raw notification docs actually need their OWN generic card
 * in the merged stack, versus which are already fully represented by a more
 * specific card (an ack-required class post, an open task) or by a wholly
 * separate surface (a studio announcement/bulletin, which keeps its own
 * alert-tier treatment per the ruling's one named exception).
 *
 * Kept here, not inlined in the page, so the dedupe rule is testable without
 * Firebase or React - the exact bug class the inventory found (a task
 * appearing in both `openTasks` and the raw notification stream, double
 * counted) is a one-line regression here if it ever comes back.
 */

import { notificationMatchesBulletin, notificationMatchesTask, type BulletinCardRef } from './notificationCompletion.ts';
import {
  bulletinAckedAtCurrentEpoch,
  bulletinDismissedAtCurrentEpoch,
  bulletinReactedAtCurrentEpoch,
  type BulletinEpochFields,
} from './bulletinEpoch.ts';

/** The subset of NotificationDoc this module reads. Kept structural so
 *  this file never needs to import the full notifications.ts type. */
export interface MergeableNotification {
  id: string;
  kind: string;
  /** Whose card it is, and where it opens; the legacy award fold reads both (notificationMatchesTask). */
  uid?: unknown;
  taskId?: string;
  link?: unknown;
  classPostId?: string;
  /** Set on a card for one REPLY under a class post, not the post itself. */
  classReplyId?: string;
  compBoardPostId?: string;
  compBoardReplyId?: string;
  bulletinId?: string;
  resendEpoch?: unknown;
  title?: unknown;
  createdAt?: unknown;
  manuallyUnreadAt?: unknown;
}

/**
 * WHAT THE VIEWER HAS ALREADY DONE AT THE SOURCE, for the cards that were
 * minted before the clearing fix and are still sitting unread.
 *
 * Live report, 2026-09-24 4:45pm Central, a comp parent on her phone: "It's
 * consistently adding notifications but I've marked everything done. They
 * keep coming back up." Acknowledging, reacting to or dismissing a class post
 * wrote only the post, never its bell card, and "Done with this" (the one
 * control that clears the card) is hidden while an acknowledgement is owed.
 * So once she tapped Understood/complete the post left the owed set and its
 * never-cleared card started counting in its place: the number on her home
 * screen never went down. lib/classPostActions.ts clears the card from now
 * on. This is the other half: a card already stranded by the old behaviour
 * stops counting the moment its source says she handled it. Count side only,
 * no writes, because a pass that writes on her behalf is exactly what the
 * heal-pass warning at the top of lib/notificationReconcile.ts rules out.
 *
 * Both halves are optional so every caller that has none of this data keeps
 * its old answer.
 */
export interface HandledSources {
  /**
   * classPostId -> the moment (ms) this viewer acknowledged or dismissed that
   * post, or ANSWERED_WITHOUT_A_TIME when all she left on it is her own emoji.
   * Built by classPostHandledTimes below from the posts the caller already
   * has loaded. A post the viewer has not handled is simply absent.
   */
  classPostHandledMs?: ReadonlyMap<string, number>;
  /** Studio announcements this viewer has already acknowledged, dismissed or
   *  answered with her emoji at the announcement's current Show again round. */
  handledBulletins?: readonly HandledBulletinRef[];
  /**
   * taskId -> the moment (ms) the viewer ticked that to-do, or every assignee
   * had. Built by taskHandledTimes below. A card about the to-do minted
   * BEFORE that moment is handled (the assignment, a due reminder); one
   * minted after it is news she has not read (a pick sent back after the
   * close, a morning reminder over a to-do ticked by hand).
   */
  taskHandledMs?: ReadonlyMap<string, number>;
}

/**
 * HER EMOJI, WHICH KEEPS NO TIME OF ITS OWN (Bobbie, 2026-10-25, decision
 * card, her pick "Count as acknowledged (Recommended)"). A family who tapped
 * an emoji on a post or announcement before an emoji also stamped the ack
 * HAS answered it, so its alert is not new, and the home bell dot goes down
 * with Waiting on you (refute round 2 of Kate's report JUA7pg2G: the number
 * and Messages had let S1 and S2 go while the bell kept counting both).
 *
 * The emoji is a mark with no moment, so it reads as older than anything
 * that has one, the way Messages files the post (Messages.tsx
 * classPostHandledByMe): the item's own alert is answered, while a reply
 * alert, and an alert she marked unread herself, are newer and keep counting.
 */
export const ANSWERED_WITHOUT_A_TIME = 0;

/** One announcement the viewer has handled, and when (ms), or
 *  ANSWERED_WITHOUT_A_TIME for her emoji alone at the current round. */
export interface HandledBulletinRef extends BulletinCardRef {
  handledMs: number;
}

/** Firestore Timestamp (or anything shaped like one) to ms; 0 when unknown. */
function timestampLikeMs(value: unknown): number {
  if (!value || typeof value !== 'object') return 0;
  const toMillis = (value as { toMillis?: unknown }).toMillis;
  if (typeof toMillis !== 'function') return 0;
  const ms = toMillis.call(value);
  return typeof ms === 'number' && Number.isFinite(ms) ? ms : 0;
}

/** Just the ClassPostDoc fields classPostHandledTimes reads. */
export interface HandledClassPostFields {
  id: string;
  acks?: Record<string, unknown>;
  dismissedBy?: Record<string, unknown>;
  reactions?: Record<string, unknown>;
}

/**
 * When this viewer acknowledged or dismissed each post, for
 * HandledSources.classPostHandledMs. On a post that asks for an
 * acknowledgement, a reaction today stamps the acks slot in the same write
 * (lib/classPosts.ts reactToClassPost), so that time is used. Her emoji with
 * no time beside it (one from before an emoji also stamped the ack, or one on
 * a post that asks for nothing) still answers the post: it is entered as
 * ANSWERED_WITHOUT_A_TIME, see there. A dancer's emoji lives in
 * dancerReactions and is never read here. An ack or dismissal still waiting
 * on the server clock reads as not handled yet: the post is then still owed
 * and counted as the post, never twice.
 */
export function classPostHandledTimes(
  posts: readonly HandledClassPostFields[],
  viewerUid: string
): Map<string, number> {
  const handled = new Map<string, number>();
  if (!viewerUid) return handled;
  for (const post of posts) {
    const ms = Math.max(
      timestampLikeMs(post.acks?.[viewerUid]),
      timestampLikeMs(post.dismissedBy?.[viewerUid])
    );
    if (ms > 0) handled.set(post.id, Math.max(ms, handled.get(post.id) ?? 0));
    else if (post.reactions?.[viewerUid] && !handled.has(post.id)) handled.set(post.id, ANSWERED_WITHOUT_A_TIME);
  }
  return handled;
}

/** Just the TaskDoc fields taskHandledTimes reads. */
export interface HandledTaskFields {
  id: string;
  assigneeUids: readonly string[];
  completedBy: Readonly<Record<string, unknown>>;
}

/**
 * WHEN SHE FINISHED EACH TO-DO, for HandledSources.taskHandledMs. Her own
 * tick's time when she holds one; otherwise, for a to-do done by every
 * assignee (the way isTaskDone reads done), the last tick's time. Messages
 * draws a row only for a to-do that still needs the viewer, so the cards
 * about a to-do she finished (the assignment, a due reminder) have no row to
 * fold behind; this is what files them under Past instead of raising them
 * in Main again (refute round on the DECISIONS 170 build, P2; Bobbie,
 * 2026-09-24: "I've marked everything done. They keep coming back up."). A
 * to-do still open for her, or for someone else with no tick of hers, is
 * absent.
 */
export function taskHandledTimes(
  tasks: readonly HandledTaskFields[],
  viewerUid: string
): Map<string, number> {
  const handled = new Map<string, number>();
  for (const task of tasks) {
    const mine = viewerUid ? timestampLikeMs(task.completedBy[viewerUid]) : 0;
    if (mine > 0) {
      handled.set(task.id, mine);
      continue;
    }
    if (task.assigneeUids.length === 0) continue;
    let last = 0;
    for (const assignee of task.assigneeUids) {
      const ms = timestampLikeMs(task.completedBy[assignee]);
      if (ms <= 0) { last = 0; break; }
      last = Math.max(last, ms);
    }
    if (last > 0) handled.set(task.id, last);
  }
  return handled;
}

/** Just the BulletinDoc fields handledBulletinRefs reads. */
export interface HandledBulletinFields extends BulletinEpochFields {
  id: string;
  title: string;
  createdAt?: unknown;
}

/**
 * The announcements this viewer has acknowledged, dismissed, or answered with
 * her emoji AT THEIR CURRENT Show again round, with when, for
 * HandledSources.handledBulletins. An answer from before a Show again does
 * not count: that round's card is news she has not acted on
 * (lib/bulletinEpoch.ts). Her emoji with no ack stamp beside it is entered as
 * ANSWERED_WITHOUT_A_TIME (see there); bulletinReactedAtCurrentEpoch reads
 * such an emoji as the first round, so it never answers a later one.
 */
export function handledBulletinRefs(
  bulletins: readonly HandledBulletinFields[],
  viewerUid: string
): HandledBulletinRef[] {
  if (!viewerUid) return [];
  const refs: HandledBulletinRef[] = [];
  for (const bulletin of bulletins) {
    const ackMs = bulletinAckedAtCurrentEpoch(bulletin, viewerUid)
      ? timestampLikeMs(bulletin.acks?.[viewerUid])
      : 0;
    const dismissMs = bulletinDismissedAtCurrentEpoch(bulletin, viewerUid)
      ? timestampLikeMs(bulletin.dismissedBy?.[viewerUid])
      : 0;
    const timedMs = Math.max(ackMs, dismissMs);
    const handledMs = timedMs > 0
      ? timedMs
      : bulletinReactedAtCurrentEpoch(bulletin, viewerUid) ? ANSWERED_WITHOUT_A_TIME : -1;
    if (handledMs < 0) continue;
    refs.push({
      id: bulletin.id,
      title: bulletin.title,
      createdAtMs: timestampLikeMs(bulletin.createdAt),
      resendEpoch: bulletin.resendEpoch ?? 0,
      handledMs,
    });
  }
  return refs;
}

/**
 * Whether this card is about something the viewer has already handled at its
 * source, so it must not count as waiting on her.
 *
 * A class post card (no classReplyId) is handled once the post is. A card for
 * a REPLY is handled only when she acted at or after the moment it was
 * minted: a reply that lands after her acknowledgement is new, and still
 * hers to read. A card she deliberately marked unread after acting keeps
 * counting, the same respect notificationReconcile.ts gives manuallyUnreadAt.
 * Her emoji with no time (ANSWERED_WITHOUT_A_TIME, 0) handles the post's own
 * card and nothing newer: no reply card is minted at or before 0, and any
 * Mark unread is later than 0.
 */
export function notificationHandledAtSource(
  notification: MergeableNotification,
  handled: HandledSources
): boolean {
  const manualUnreadMs = notification.manuallyUnreadAt === undefined
    ? 0
    : notification.manuallyUnreadAt === null
      ? Number.POSITIVE_INFINITY
      : timestampLikeMs(notification.manuallyUnreadAt) || Number.POSITIVE_INFINITY;
  const classPostId = notification.classPostId;
  if (classPostId && handled.classPostHandledMs) {
    const handledMs = handled.classPostHandledMs.get(classPostId);
    if (handledMs !== undefined && manualUnreadMs <= handledMs) {
      if (!notification.classReplyId) return true;
      const createdMs = timestampLikeMs(notification.createdAt);
      if (createdMs > 0 && createdMs <= handledMs) return true;
    }
  }
  if (handled.handledBulletins && handled.handledBulletins.length > 0) {
    const createdMs = timestampLikeMs(notification.createdAt);
    for (const bulletin of handled.handledBulletins) {
      if (!notificationMatchesBulletin(notification, bulletin)) continue;
      if (manualUnreadMs > bulletin.handledMs) continue;
      // A card minted after she acted belongs to a later round she has not
      // seen. notificationMatchesBulletin already refuses a card from a Show
      // again round newer than the one she handled. Her emoji with no time
      // (ANSWERED_WITHOUT_A_TIME) has no moment to compare against, and needs
      // none: it answers the current round only, the match above already
      // refuses a later round's stamped card, an unstamped emoji reads as the
      // first round (so no later round exists for an unstamped card to come
      // from), and an announcement mints no reply cards.
      if (bulletin.handledMs !== ANSWERED_WITHOUT_A_TIME && createdMs > bulletin.handledMs) continue;
      return true;
    }
  }
  if (handled.taskHandledMs && handled.taskHandledMs.size > 0 && (notification.kind === 'task' || notification.taskId)) {
    // A card about a to-do she finished, minted before she finished it. A
    // card minted after her tick is news: a pick sent back after the close,
    // or a morning reminder over a to-do ticked by hand with no pick sent
    // (DECISIONS 170). A card with no time yet (its write still pending) is
    // new by definition and stays unread.
    const createdMs = timestampLikeMs(notification.createdAt);
    if (createdMs > 0) {
      for (const [taskId, handledMs] of handled.taskHandledMs) {
        if (!notificationMatchesTask(notification, taskId)) continue;
        if (manualUnreadMs <= handledMs && createdMs <= handledMs) return true;
      }
    }
  }
  return false;
}

/**
 * ONE CARD PER COMP BOARD POST, the newest (the first one met, because every
 * caller's stream is newest first). Every reply mints its own card for every
 * comp family (notificationAuthority.mjs), and Messages shows a post's cards
 * as one row, so every number that counts cards counts the row, not the pile:
 * the "Waiting on you" count (dedupeMergedNotifications below) and the home
 * bell's own label (FamilyCommandCenter.tsx alertsCount, refute round 1: the
 * bell said "4 new alerts" over one comp board row). Every other card passes
 * through untouched. Order kept.
 */
export function collapseCompBoardCardsPerPost<T extends Pick<MergeableNotification, 'compBoardPostId'>>(
  notifications: readonly T[]
): T[] {
  const seen = new Set<string>();
  return notifications.filter((notification) => {
    const postId = notification.compBoardPostId;
    if (!postId) return true;
    if (seen.has(postId)) return false;
    seen.add(postId);
    return true;
  });
}

/** Drop every card whose source the viewer has already handled. Order kept. */
export function withoutHandledSourceCards<T extends MergeableNotification>(
  notifications: readonly T[],
  handled: HandledSources
): T[] {
  if (!handled.classPostHandledMs?.size && !handled.handledBulletins?.length) {
    return [...notifications];
  }
  return notifications.filter((notification) => !notificationHandledAtSource(notification, handled));
}

/**
 * Which raw, unread notification docs need their own generic merged card.
 *
 * - 'bulletin' kind is EXCLUDED outright: a studio announcement already has
 *   its own dedicated, richer surface (BulletinAckCard / the non-ack
 *   bulletin row, both driven by useAttentionBulletins, not by this raw
 *   stream) and keeps the alert-tier treatment the ruling reserves for a
 *   true announcement from Kim. Showing it a second time here would be the
 *   exact "two lists, different behavior" problem this slice exists to end.
 * - a 'task' kind card whose taskId matches an already-open task is
 *   excluded: that task already renders as its own PriorityRow (the double-
 *   count bug the inventory documented - a task appearing in both
 *   `openTasks` and the raw notification stream).
 * - any card whose classPostId matches an already-rendered ack-required
 *   class post is excluded: AckMessageCard already shows it, full content,
 *   with its own Got it.
 * - any card whose source the viewer has already handled (`handled`, see
 *   HandledSources above) is excluded: an acknowledged, reacted-to or
 *   dismissed class post's card used to count in the post's place forever.
 * - comp board cards collapse to ONE per post. Every reply mints its own card
 *   for every comp family (notificationAuthority.mjs), and counting each one
 *   made a busy thread read as a pile of separate things waiting on her.
 *   Messages shows the same post as one row ("N new replies"), and Done there
 *   clears the whole pile, so the count follows the row. The newest card is
 *   kept, which is the first one here because the stream is newest first.
 *
 * Order is preserved; only membership changes.
 */
export function dedupeMergedNotifications<T extends MergeableNotification>(
  notifications: readonly T[],
  openTaskIds: ReadonlySet<string>,
  ackClassPostIds: ReadonlySet<string>,
  handled: HandledSources = {}
): T[] {
  return collapseCompBoardCardsPerPost(notifications.filter((notification) => {
    if (notification.kind === 'bulletin') return false;
    if (
      notification.kind === 'task'
      && notification.taskId
      && openTaskIds.has(notification.taskId)
    ) {
      return false;
    }
    if (notification.classPostId && ackClassPostIds.has(notification.classPostId)) {
      return false;
    }
    if (notificationHandledAtSource(notification, handled)) return false;
    return true;
  }));
}

/**
 * The merged stack's stable per-card key, for the dedupe-against-itself
 * guard (a card must never be counted twice) and for the "already
 * handled this visit" bridge set (see justHandledCardKeys in
 * FamilyCommandCenter.tsx, the same keep-it-visible-so-undo-stays-reachable
 * pattern justReadIds already used for the alert rows this replaces).
 */
export function mergedNotifCardKey(notificationId: string): string {
  return `notif:${notificationId}`;
}
